import React from "react";
import { formatTier, tierKey } from "../../lib/tierLabel";
import { Badge } from "../ui/badge";
import { Sparkles } from "lucide-react";

// C1/C2 — BIA workspace column configuration (extracted from OperationalBIAPage
// for reuse/testability). Behavior is identical to the original inline version.
export const BIA_COLS_KEY = "bia_cols_v1";

// Fixed left-to-right order; visibility toggled per column. Actions is always trailing/sticky.
export const COLUMN_ORDER = [
  "code", "dept_scope", "department", "process_name", "facility", "equipment",
  "applications", "upstream", "downstream", "tier", "rto", "rpo", "status",
  "team_roles", "vendors", "data_stores", "gaps",
];

// label + whether opt-in (hidden by default). optIn columns default to false.
export const COLUMN_META = {
  code: { label: "Code" },
  dept_scope: { label: "Dept ID / IT Scope", optIn: true },
  department: { label: "Department" },
  process_name: { label: "Process Name" },
  facility: { label: "Facility" },
  equipment: { label: "Equipment" },
  applications: { label: "Applications" },
  upstream: { label: "Upstream" },
  downstream: { label: "Downstream" },
  tier: { label: "Tier" },
  rto: { label: "RTO" },
  rpo: { label: "RPO" },
  status: { label: "Status" },
  team_roles: { label: "Team & Roles", optIn: true },
  vendors: { label: "Vendors", optIn: true },
  data_stores: { label: "Data Stores", optIn: true },
  gaps: { label: "Gaps", optIn: true },
};

export const DEFAULT_COL_VIS = COLUMN_ORDER.reduce((acc, k) => {
  acc[k] = !COLUMN_META[k].optIn;
  return acc;
}, {});

const getTierColor = (tier) => ({
  tier_1: "bg-red-100 text-red-700 border-red-200",
  tier_2: "bg-orange-100 text-orange-700 border-orange-200",
  tier_3: "bg-amber-100 text-amber-700 border-amber-200",
  tier_4: "bg-slate-100 text-slate-600 border-slate-200",
}[tier] || "bg-slate-100 text-slate-600 border-slate-200");

const Chips = ({ items, testid }) => {
  const arr = (items || []).filter(Boolean);
  if (arr.length === 0) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {arr.slice(0, 3).map((label, i) => (
        <Badge key={i} variant="outline" className="text-[10px] bg-slate-50 border-slate-200 text-slate-600 whitespace-nowrap">{label}</Badge>
      ))}
      {arr.length > 3 && <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500">+{arr.length - 3}</Badge>}
    </div>
  );
};

const depNames = (details, ids, custom) => {
  const names = [];
  if (details && typeof details === "object") {
    Object.values(details).forEach((d) => { if (d && (d.process_name || d.name)) names.push(d.process_name || d.name); });
  }
  if (names.length === 0 && Array.isArray(ids)) ids.forEach((i) => names.push(i));
  (custom || []).forEach((c) => names.push(c));
  return names;
};

export const columnHeaderLabel = (key, biaType) => {
  if (key === "dept_scope") return biaType === "technical" ? "IT Function Scope" : "Dept ID";
  if (key === "code") return "Process ID";
  if (key === "process_name") return biaType === "technical" ? "Technical Process" : "Process Name";
  return COLUMN_META[key].label;
};

// ctx: { biaType, applicationProfiles, getRTOBandColor }
export const renderBiaCell = (key, process, ctx) => {
  const { biaType, applicationProfiles, getRTOBandColor } = ctx;
  const appName = (id) => {
    const p = applicationProfiles.find((a) => a.id === id);
    return p ? (p.application_name || id) : id;
  };
  switch (key) {
    case "code":
      return process.process_code
        ? <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-xs font-mono">{process.process_code}</Badge>
        : <span className="text-slate-400 text-xs">—</span>;
    case "dept_scope":
      if (biaType === "technical") {
        return process.it_function_scope
          ? <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-xs uppercase tracking-wide">{process.it_function_scope}</Badge>
          : <span className="text-slate-400 text-xs">—</span>;
      }
      return process.dept_id
        ? <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-xs font-mono">{process.dept_id}</Badge>
        : <span className="text-slate-400 text-xs">—</span>;
    case "department":
      return process.department || <span className="text-slate-400 text-xs">—</span>;
    case "process_name":
      return (
        <div className="flex items-center gap-2 font-medium">
          <span>{process.process_name}</span>
          {process.source === "onboarding_enrichment" && (
            <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] gap-1 whitespace-nowrap" title="Auto-created from onboarding enrichment." data-testid={`auto-created-badge-${process.id}`}>
              <Sparkles className="w-3 h-3" /> Auto-created
            </Badge>
          )}
        </div>
      );
    case "facility": {
      const facs = (process.facility_dependencies || []).map((f) => f.name || f.facility_name || f.facility_id || f.id || f);
      return <Chips items={facs} testid={`col-facility-${process.id}`} />;
    }
    case "equipment":
      return <Chips items={process.custom_application_dependencies} testid={`col-equipment-${process.id}`} />;
    case "applications":
      return <Chips items={(process.application_dependencies || []).map(appName)} testid={`col-applications-${process.id}`} />;
    case "upstream":
      return <Chips items={depNames(process.upstream_dependency_details, process.upstream_process_ids, process.custom_upstream_dependencies)} testid={`col-upstream-${process.id}`} />;
    case "downstream":
      return <Chips items={depNames(process.downstream_dependency_details, process.downstream_process_ids, process.custom_downstream_dependencies)} testid={`col-downstream-${process.id}`} />;
    case "tier": {
      const tk = tierKey(process.criticality_tier);
      return tk
        ? <Badge variant="outline" className={`${getTierColor(tk)} text-[10px] uppercase`}>{formatTier(process.criticality_tier)}</Badge>
        : <span className="text-slate-400 text-xs">—</span>;
    }
    case "rto":
      return <Badge className={getRTOBandColor(process.calculated_rto_band)}>{process.calculated_rto_hours}h</Badge>;
    case "rpo":
      return <span>{process.calculated_rpo_hours}h</span>;
    case "status":
      return <Badge variant="outline" className="capitalize">{(process.status || "draft").replace("_", " ")}</Badge>;
    case "team_roles": {
      const roles = (process.staffing_roles || []).map((r) => `${r.role_name || r.role || "Role"} ${r.minimum_staff ?? r.min ?? 0}/${r.total_staff ?? r.total ?? 0}`);
      return <Chips items={roles} testid={`col-team-roles-${process.id}`} />;
    }
    case "vendors": {
      const vs = (process.vendor_dependencies || []).map((v) => v.name || v.vendor_name || v.vendor_id || v.id || v);
      return <Chips items={vs} testid={`col-vendors-${process.id}`} />;
    }
    case "data_stores": {
      const ds = (process.asset_data_sensitivity || [])
        .filter((s) => s.holds_sensitive_data)
        .map((s) => `${s.asset_name || s.asset_ref}${s.asset_rpo_hours != null ? ` (${s.asset_rpo_hours}h)` : ""}`);
      return <Chips items={ds} testid={`col-data-stores-${process.id}`} />;
    }
    case "gaps":
      return process.gap_count > 0
        ? <Badge variant="destructive">{process.gap_count}</Badge>
        : <Badge variant="outline" className="text-emerald-600">0</Badge>;
    default:
      return null;
  }
};
